// pages/BusinessAccounts.tsx
import React from "react";
import { Link } from "react-router-dom";
import Button from "../components/Button";
import DataTableView from "../components/DataTableView2";
import DatePicker from "../components/DatePicker";
import EnlargedImage from "../components/Image";
import Input from "../components/Input";
import Select from "../components/Select";
import { formatDate } from "../utils/helpers";
import { IBussinessAccountDocs } from "../utils/types";
import useBusinessAccounts from '../hooks/logics/user-accounts/use-business-dashboard';

const BusinessAccounts = () => {
  const {
    businessAccounts,
    totalPages,
    loading,
    allStates,
    isLoadingStates,
    zoomed,
    setZoomed,
    image,
    key,
    search,
    fromDate,
    toDate,
    state,
    searchParams,
    filterFormRef,
    searchFormRef,
    updateUrlParams,
    handleSearch,
    handleFilter,
    handleResetFilters,
    handleZoomImage,
    isApplyButtonDisabled,
    isClearButtonDisabled,
  } = useBusinessAccounts();

  const columns = [
    {
      header: "Username",
      className: "md:p-0 md:pl-10",
      accessor: (user: IBussinessAccountDocs) => (
        <Link to={`/dashboard/business/${user._id}`} className="flex items-center cursor-pointer md:!pl-5 md:w-[200px]">
          <div className="flex-shrink-0 w-8 h-8 border border-black rounded-full">
            <img
              onClick={(e) => { e.preventDefault(); handleZoomImage(user.profileImage); }}
              onError={(e) => { (e.target as HTMLImageElement).src = "https://www.pngfind.com/pngs/m/610-6104451_image-placeholder-png-user-profile-placeholder-image-png.png"; }}
              className="w-full h-full rounded-full object-cover"
              src={user.profileImage}
              alt={user.fullName}
            />
          </div>
          <p className="ml-2 truncate text-md text-black font-normal">{user.fullName}</p>
        </Link>
      ),
    },
    {
      header: "Email",
      accessor: (user: IBussinessAccountDocs) => (
        <p className="w-44 2xl:w-72 text-black font-normal truncate mr-2 text-md">{user.email}</p>
      ),
    },
    {
      header: "Business name",
      accessor: (user: IBussinessAccountDocs) => (
        <div className="md:w-[150px]">{user.businessName || "N/A"}</div>
      ),
    },
    {
      header: "Phone",
      accessor: (user: IBussinessAccountDocs) => (
        <div className="md:w-[200px]">{user.phone}</div>
      ),
    },
    {
      header: "Referred by",
      accessor: (user: IBussinessAccountDocs) =>
        user.referredBy?._id && user.referredBy.email
          ? <Link to={`/dashboard/users/${user.referredBy._id}`}><p>{user.referredBy.email}</p></Link>
          : <p>N/A</p>,
    },
    {
      header: "State",
      accessor: (user: IBussinessAccountDocs) => (
        <div className="md:w-[100px]">{user.state || "N/A"}</div>
      ),
    },
    {
      header: "Referrer points",
      accessor: (user: IBussinessAccountDocs) => (
        <div className="md:w-[150px]">{user.points || "N/A"}</div>
      ),
    },
    {
      header: "Total orders",
      accessor: (user: IBussinessAccountDocs) => (
        <div className="md:w-[150px]">{user.totalOrders || "N/A"}</div>
      ),
    },
    {
      header: "Completed orders",
      accessor: (user: IBussinessAccountDocs) => (
        <div className="md:w-[150px]">{user.completedOrders || "N/A"}</div>
      ),
    },
    {
      header: "Action",
      accessor: (user: IBussinessAccountDocs) => (
        <div className="flex items-center gap-4 md:pr-10">
          <Link to={`/dashboard/business/${user._id}`} className="text-primary cursor-pointer">
            Details
          </Link>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="w-full h-full flex flex-col">

        {/* Search */}
        <form className="w-full flex flex-row gap-x-2 md:gap-x-4 md:justify-end mb-4" ref={searchFormRef} onSubmit={handleSearch}>
          <Input
            name="search"
            value={search}
            onChange={(e) => updateUrlParams({ search: e.target.value })}
            className="w-full md:w-52 outline-none input-primary h-8 md:h-10 rounded-lg"
            placeholder="Search"
          />
          <Button disabled={!search} type="submit" className="w-1/3 md:w-32 min-h-0 h-8 md:h-10">
            Search
          </Button>
        </form>

        {/* Filters */}
        <form key={key} ref={filterFormRef} onSubmit={handleFilter} className="w-full flex flex-wrap gap-4 justify-end items-center mb-4">
          <div className="w-full flex flex-row gap-x-2 md:gap-x-4 md:w-max mt-4 md:mt-0">
            <div className="flex flex-col items-start md:flex-row md:items-center gap-y-1 w-1/2 md:w-full">
              <label className="mr-2">From:</label>
              <DatePicker
                name="fromDate"
                placeholderText="MM/DD/YYYY"
                selected={fromDate ? new Date(fromDate) : null}
                onChange={(e) => updateUrlParams({ fromDate: formatDate(e?.toString() || "") })}
                className="min-h-0 h-8 md:h-10 py-1"
                inputClassName="py-0 md:py-2 h-8 md:h-10"
                popperClassName="!left-7"
              />
            </div>
            <div className="flex flex-col items-start md:flex-row md:items-center gap-y-1 w-1/2 md:w-full">
              <label className="mr-2">To:</label>
              <DatePicker
                name="toDate"
                placeholderText="MM/DD/YYYY"
                selected={toDate ? new Date(toDate) : null}
                onChange={(e) => updateUrlParams({ toDate: formatDate(e?.toString() || "") })}
                className="min-h-0 h-8 md:h-10 py-1"
                inputClassName="py-0 md:py-2 h-8 md:h-10"
                popperClassName="!-left-7"
              />
            </div>
          </div>

          <div className="w-full md:w-auto flex gap-x-2 md:gap-x-4">
            <Select
              name="state"
              options={allStates}
              value={state}
              disabled={isLoadingStates}
              onChange={(e) => updateUrlParams({ state: e.target.value })}
              placeholder="States"
              className="md:w-32 outline-none bg-white rounded-lg min-h-0 h-8 md:h-10 pl-4 pr-2"
              containerClassName="w-[49%] md:w-auto"
            />
            <Button type="button" onClick={() => window.location.reload()} className="w-[51%] btn-outline flex-1 md:flex-none md:w-32 min-h-0 h-8 md:h-10">
              Refresh
            </Button>
          </div>

          <div className="w-full md:w-auto flex gap-x-2 md:gap-x-4 mb-8 md:mb-0">
            <Button type="submit" disabled={isApplyButtonDisabled} className="flex-1 md:flex-none md:w-28 min-h-0 h-8 md:h-10">
              Apply
            </Button>
            <Button type="button" disabled={isClearButtonDisabled} onClick={handleResetFilters} className="flex-1 md:flex-none md:w-24 min-h-0 h-8 md:h-10">
              Clear
            </Button>
          </div>
        </form>

        <DataTableView
          data={businessAccounts}
          columns={columns}
          isFetching={loading}
          emptyStateMessage="No business accounts found"
          currentPage={parseInt(searchParams.get("page") || "1")}
          setCurrentPage={(page: number) => updateUrlParams({ page: page.toString() })}
          totalPages={totalPages}
        />
      </div>

      <EnlargedImage isVisible={zoomed} setVisible={setZoomed} src={image} />
    </>
  );
};

export default BusinessAccounts;